import { createHash } from "node:crypto";
import { LocalCliError } from "./errors.js";
import { isHostedRegionSupported, HOSTED_AGENT_REGIONS } from "./regions.js";
import { listModelQuota, modelQuotaVerdict, isReasoningModel } from "./quota.js";
import { runAz, rethrowIfAzUnavailable } from "./az.js";
import { deriveAcrName, ensureAcr, grantAcrPullViaAz } from "./acr.js";

/**
 * Deterministic default Foundry account name: "m8t" + 12 hex of a SHA-256 of
 * the subscription id (a globally-unique GUID). Deterministic per-subscription
 * (so a re-run without --account targets the SAME account → idempotency holds)
 * and globally-unique-by-construction (the custom subdomain is global). Stays
 * within cognitiveservices naming (2-64 chars, lowercase alphanumeric).
 */
export function deriveAccountName(subscriptionId: string): string {
  const h = createHash("sha256").update(subscriptionId).digest("hex").slice(0, 12);
  return `m8t${h}`;
}

/** Throw FOUNDRY_REGION_NOT_ELIGIBLE unless the region can host Foundry agents. */
export function assertHostedRegion(location: string): void {
  if (isHostedRegionSupported(location)) return;
  throw new LocalCliError({
    code: "FOUNDRY_REGION_NOT_ELIGIBLE",
    message: `Region '${location}' is not eligible for hosted Foundry agents.`,
    hint: `Use a hosted-eligible region, e.g. ${HOSTED_AGENT_REGIONS.slice(0, 4).join(", ")}.`,
  });
}

/**
 * Check model quota in a region. Throws FOUNDRY_MODEL_NO_QUOTA on a zero-quota
 * model; returns "ok" | "unknown" otherwise (caller warns on "unknown").
 */
export async function checkModelQuota(region: string, model: string): Promise<"ok" | "unknown"> {
  const usages = await listModelQuota(region);
  const { verdict, quotad } = modelQuotaVerdict(usages, model);
  if (verdict === "no_quota") {
    throw new LocalCliError({
      code: "FOUNDRY_MODEL_NO_QUOTA",
      message: `Model '${model}' has 0 quota in ${region}.`,
      hint: `Pick a quota'd model (--model ${quotad[0] ?? "gpt-4.1-mini"}) or request quota in ${region}.`,
    });
  }
  return verdict === "ok" ? "ok" : "unknown";
}

interface AccountShow {
  id?: string;
  properties?: { customSubDomainName?: string; allowProjectManagement?: boolean };
}

export interface EnsureAccountArgs {
  account: string;
  resourceGroup: string;
  location: string;
}
export interface EnsureAccountResult {
  created: boolean;
  accountScope: string; // ARM resource id
  subdomain: string;
}

/** Ensure the AIServices account exists with a custom subdomain + project management. */
export async function ensureAccount(args: EnsureAccountArgs): Promise<EnsureAccountResult> {
  let existing: AccountShow | null = null;
  try {
    existing = JSON.parse(
      await runAz(["cognitiveservices", "account", "show", "--name", args.account, "--resource-group", args.resourceGroup, "--output", "json"]),
    ) as AccountShow;
  } catch (e) {
    rethrowIfAzUnavailable(e);
    existing = null; // absent — the idempotent create below is the backstop
  }

  if (existing?.properties?.customSubDomainName && existing.properties.allowProjectManagement) {
    return { created: false, accountScope: existing.id ?? "", subdomain: existing.properties.customSubDomainName };
  }

  let createdOut: AccountShow;
  try {
    createdOut = JSON.parse(
      await runAz([
        "cognitiveservices", "account", "create",
        "--name", args.account,
        "--resource-group", args.resourceGroup,
        "--location", args.location,
        "--kind", "AIServices",
        "--sku", "S0",
        "--custom-domain", args.account,
        "--allow-project-management",
        "--yes",
        "--output", "json",
      ]),
    ) as AccountShow;
  } catch (e) {
    const msg = e instanceof Error ? e.message : String(e);
    // Match ONLY the az error text, not the whole runAz message. runAz formats a
    // failure as `'az … --custom-domain …' failed: <stderr>`, so testing the full
    // string matches the literal `--custom-domain` flag in the COMMAND and would
    // mislabel EVERY create failure (missing RG, quota exhaustion, permissions,
    // unsupported region) as a subdomain conflict. Slice off the command first,
    // then match the real ARM signature (`CustomDomainInUse`).
    const sep = "' failed: ";
    const stderr = msg.includes(sep) ? msg.slice(msg.indexOf(sep) + sep.length) : msg;
    if (/CustomDomainInUse|subdomain name .* is not available|already used by a resource/i.test(stderr)) {
      throw new LocalCliError({
        code: "FOUNDRY_ACCOUNT_SUBDOMAIN_CONFLICT",
        message: `The custom subdomain '${args.account}' is unavailable (globally taken).`,
        hint: "Pick a different --account name. If you recently deleted an account with this name, purge it: 'az cognitiveservices account purge --location <region> --resource-group <rg> --name <name>'.",
      });
    }
    throw e; // a real error (ResourceGroupNotFound / quota / permissions) — surface it verbatim
  }

  if (!createdOut.properties?.customSubDomainName || !createdOut.properties.allowProjectManagement) {
    throw new LocalCliError({
      code: "FOUNDRY_ACCOUNT_NOT_CONFORMING",
      message: `Account '${args.account}' exists but lacks a custom subdomain or project management, and could not be converged.`,
      hint: "Use a fresh --account name or resource group.",
    });
  }
  return { created: !existing, accountScope: createdOut.id ?? "", subdomain: createdOut.properties.customSubDomainName };
}

interface ProjectShow {
  name?: string;
  identity?: { principalId?: string };
  properties?: { endpoints?: Record<string, string> };
}

export interface EnsureProjectArgs {
  account: string;
  resourceGroup: string;
  project: string;
  location: string;
}
export interface EnsureProjectResult {
  created: boolean;
  endpoint: string;
  projectPrincipalId: string | null;
}

function readProjectEndpoint(show: ProjectShow): string {
  const ep = show.properties?.endpoints?.["AI Foundry API"];
  if (!ep) {
    throw new LocalCliError({
      code: "FOUNDRY_PROJECT_NO_ENDPOINT",
      message: "Project created but no 'AI Foundry API' endpoint was returned.",
      hint: "Re-run after a moment, or check 'az cognitiveservices account project show'.",
    });
  }
  return ep;
}

const projectShowArgs = (a: EnsureProjectArgs): string[] => [
  "cognitiveservices", "account", "project", "show",
  "--name", a.account, "--resource-group", a.resourceGroup, "--project-name", a.project, "--output", "json",
];

/** Ensure the project exists, then read its canonical endpoint + MI principal id. */
export async function ensureProject(args: EnsureProjectArgs): Promise<EnsureProjectResult> {
  let show: ProjectShow | null = null;
  try {
    show = JSON.parse(await runAz(projectShowArgs(args))) as ProjectShow;
  } catch (e) {
    rethrowIfAzUnavailable(e);
    show = null; // absent — the idempotent create below is the backstop
  }
  if (show) {
    return { created: false, endpoint: readProjectEndpoint(show), projectPrincipalId: show.identity?.principalId ?? null };
  }

  await runAz([
    "cognitiveservices", "account", "project", "create",
    "--name", args.account, "--resource-group", args.resourceGroup,
    "--project-name", args.project, "--location", args.location, "--output", "json",
  ]);
  const created = JSON.parse(await runAz(projectShowArgs(args))) as ProjectShow;
  return { created: true, endpoint: readProjectEndpoint(created), projectPrincipalId: created.identity?.principalId ?? null };
}

interface DeploymentShow { sku?: { capacity?: number } }

export interface EnsureDeploymentArgs {
  account: string;
  resourceGroup: string;
  model: string;
  modelVersion: string;
  capacity: number;
  /**
   * Deployment model format — "OpenAI" (default), "xAI", … Sourced per-rung from
   * the model catalog. Defaults to "OpenAI" so every pre-existing caller emits a
   * byte-identical az argv; a test pins that.
   */
  format?: string;
  onProgress?: (msg: string) => void;
}
export interface EnsureDeploymentResult { created: boolean }

/** Ensure the model deployment exists at >= the requested capacity (converge up, never down). */
export async function ensureDeployment(args: EnsureDeploymentArgs): Promise<EnsureDeploymentResult> {
  let current: number | null = null;
  try {
    const show = JSON.parse(
      await runAz(["cognitiveservices", "account", "deployment", "show", "--name", args.account, "--resource-group", args.resourceGroup, "--deployment-name", args.model, "--output", "json"]),
    ) as DeploymentShow;
    current = show.sku?.capacity ?? null;
  } catch (e) {
    rethrowIfAzUnavailable(e);
    current = null; // absent — the idempotent create below is the backstop
  }
  if (current !== null && current >= args.capacity) return { created: false };
  if (current !== null && current < args.capacity) args.onProgress?.(`scaling ${args.model} ${String(current)}→${String(args.capacity)}`);

  await runAz([
    "cognitiveservices", "account", "deployment", "create",
    "--name", args.account, "--resource-group", args.resourceGroup,
    "--deployment-name", args.model, "--model-name", args.model,
    "--model-version", args.modelVersion, "--model-format", args.format ?? "OpenAI",
    "--sku-name", "GlobalStandard", "--sku-capacity", String(args.capacity), "--output", "json",
  ]);
  return { created: current === null };
}

export interface CreateFoundryArgs {
  resourceGroup: string;
  location: string;
  account: string;
  project: string;
  model: string;
  modelVersion: string;
  capacity: number;
  skipQuotaCheck: boolean;
  onProgress?: (msg: string) => void;
}

export interface CreateFoundryResult {
  endpoint: string;
  accountName: string;
  accountScope: string;
  projectName: string;
  region: string;
  projectPrincipalId: string | null;
  model: string;
  capacity: number;
  created: { account: boolean; project: boolean; deployment: boolean };
}

const REASONING_MIN_CAPACITY = 250;

/** Create (idempotently) a Foundry account + project + model deployment from zero. */
export async function createFoundryProject(args: CreateFoundryArgs): Promise<CreateFoundryResult> {
  // 1. Guards — BEFORE any mutation.
  assertHostedRegion(args.location);
  if (!args.skipQuotaCheck) {
    const verdict = await checkModelQuota(args.location, args.model);
    if (verdict === "unknown") {
      args.onProgress?.(`could not confirm quota for '${args.model}' in ${args.location}; proceeding`);
    }
  }
  if (isReasoningModel(args.model) && args.capacity < REASONING_MIN_CAPACITY) {
    args.onProgress?.(`note: reasoning model '${args.model}' at capacity ${String(args.capacity)} may 429; consider --capacity ${String(REASONING_MIN_CAPACITY)}`);
  }

  // 2. Account → 3. Project → 4. Deployment.
  args.onProgress?.(`ensuring Foundry account ${args.account}…`);
  const acct = await ensureAccount({ account: args.account, resourceGroup: args.resourceGroup, location: args.location });
  args.onProgress?.(`ensuring project ${args.project}…`);
  const proj = await ensureProject({ account: args.account, resourceGroup: args.resourceGroup, project: args.project, location: args.location });
  args.onProgress?.(`ensuring model deployment ${args.model} (capacity ${String(args.capacity)})…`);
  const dep = await ensureDeployment({
    account: args.account, resourceGroup: args.resourceGroup,
    model: args.model, modelVersion: args.modelVersion, capacity: args.capacity, onProgress: args.onProgress,
  });

  // 5. Image registry — hosted agents on post-2026-06-25 projects require an
  //    authenticated pull (project MI AcrPull). Stand up the ACR + grant up front;
  //    the deploy step also create-if-missing + re-grants AcrPull as a self-heal.
  args.onProgress?.("ensuring hosted-agent image registry (ACR)…");
  const acrName = deriveAcrName(args.account);
  const acr = await ensureAcr({ acrName, resourceGroup: args.resourceGroup, location: args.location });
  if (proj.projectPrincipalId && acr.resourceId) {
    try {
      await grantAcrPullViaAz({ acrResourceId: acr.resourceId, principalId: proj.projectPrincipalId });
    } catch (e) {
      rethrowIfAzUnavailable(e);
      // Non-fatal: a grant failure (e.g. the caller lacks role-assignment rights) is
      // re-attempted by the deploy step's preconditions, which surfaces a clear
      // permission error if the operator truly can't assign roles.
      args.onProgress?.(`could not grant AcrPull to the project MI now; the deploy step will complete it (${e instanceof Error ? e.message : String(e)})`);
    }
  } else {
    args.onProgress?.("project managed identity not ready yet; AcrPull grant deferred to the deploy step.");
  }

  return {
    endpoint: proj.endpoint,
    accountName: args.account,
    accountScope: acct.accountScope,
    projectName: args.project,
    region: args.location,
    projectPrincipalId: proj.projectPrincipalId,
    model: args.model,
    capacity: args.capacity,
    created: { account: acct.created, project: proj.created, deployment: dep.created },
  };
}
